import { useEffect, useRef, useState } from "react";
import { Gauge, Pause, TrendingDown } from "lucide-react";
import { C, FONT, S, T, hexA, mmss } from "../theme";
import type { NextRun } from "../lib/nextRun";

/** Live mm:ss that ticks every second from the last snapshot, re-baselining when
 *  the snapshot moves — so the watering countdown advances between 5s polls. */
function useLiveRemaining(seconds: number | undefined, active: boolean) {
  const base = useRef({ wall: Date.now(), s: seconds ?? 0 });
  const [s, setS] = useState(seconds ?? 0);
  useEffect(() => {
    base.current = { wall: Date.now(), s: seconds ?? 0 };
    setS(seconds ?? 0);
  }, [seconds]);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => {
      setS(Math.max(0, base.current.s - (Date.now() - base.current.wall) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [active]);
  return s;
}

/**
 * The Overview "next run" hero. Renders the single canonical NextRun answer
 * (shared with the Schedule hero) so the two surfaces never disagree on the
 * next zone, its timing, or its reasoning.
 */
export function Hero({ nr }: { nr: NextRun }) {
  const watering = nr.kind === "watering";
  const liveRem = useLiveRemaining(nr.zone?.remaining, watering);
  const eyebrow = watering ? "Watering now" : nr.kind === "paused" ? "Automatic watering" : "Next scheduled run";

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${C.paper} 0%, ${C.raise} 100%)`,
        border: `1px solid ${C.line}`,
        borderRadius: "var(--radius-card-lg)",
        boxShadow: "var(--shadow-card)",
        padding: S[6],
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: S[3] }}>
        <span className="eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: S[2] }}>
          {watering && <span className="dot-pulse" style={{ width: 8, height: 8, borderRadius: 99, background: C.sea }} />}
          {eyebrow}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: T.sm, color: C.textMid }}>
          <Gauge size={13} style={{ color: C.sage }} /> ET-balanced
        </span>
      </div>

      {nr.kind === "paused" ? (
        <div style={{ display: "flex", alignItems: "center", gap: S[3], marginTop: S[4], color: C.ice }}>
          <Pause size={20} />
          <span style={{ fontFamily: FONT.serif, fontSize: T.xl }}>Automatic watering paused</span>
        </div>
      ) : nr.kind === "none" ? (
        <div style={{ marginTop: S[4], fontSize: T.base, color: C.textMid }}>{nr.why}</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: S[4], marginTop: S[3] }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: S[3], flexWrap: "wrap" }}>
                <span style={{ fontFamily: FONT.serif, fontSize: T.d1, fontWeight: 500, color: C.ink, lineHeight: 1 }}>
                  {nr.name}
                </span>
                {nr.badge && (
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      color: C.ice,
                      border: `1px solid ${hexA(C.ice, 0.4)}`,
                      borderRadius: 6,
                      padding: "3px 7px",
                    }}
                  >
                    {nr.badge}
                  </span>
                )}
              </div>
              <div style={{ marginTop: S[3], fontSize: 13, color: C.textMid, maxWidth: "52ch" }}>
                {nr.why}
                {nr.soilPct != null && nr.madPct != null && (
                  <>
                    {" "}
                    Soil at{" "}
                    <span className="mono" style={{ color: C.beacon }}>
                      {nr.soilPct}%
                    </span>
                    {watering ? " — refilling." : `, refill at ${nr.madPct}%.`}
                  </>
                )}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="tnum" style={{ fontFamily: FONT.serif, fontSize: T.d3, fontWeight: 400, color: C.seaDeep, lineHeight: 0.9 }}>
                {watering ? mmss(liveRem) : nr.eta}
              </div>
              <div style={{ marginTop: S[1], fontSize: T.xs, color: C.muted, letterSpacing: ".04em" }}>
                {watering ? nr.etaCaption : `${nr.whenLabel} · ${nr.etaCaption}`}
              </div>
            </div>
          </div>

          {nr.savingsPct != null && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginTop: S[4],
                padding: "4px 10px",
                borderRadius: "var(--radius-pill)",
                background: hexA(C.sageDeep, 0.12),
                color: C.sageDeep,
                fontSize: T.sm,
                fontWeight: 600,
              }}
            >
              <TrendingDown size={13} />
              {nr.savingsPct}% cheaper than a fixed 06:00 start
            </div>
          )}
        </>
      )}
    </div>
  );
}
